import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LoadingFallbackProps {
  message?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Reusable loading fallback component for Suspense boundaries
 * and async operations.
 */
export function LoadingFallback({ 
  message = 'Loading...', 
  className,
  size = 'md' 
}: LoadingFallbackProps) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  return (
    <div 
      className={cn(
        'flex flex-col items-center justify-center gap-3 p-8',
        className
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <Loader2 
        className={cn('animate-spin text-primary', sizeClasses[size])} 
        aria-hidden="true"
      />
      <span className="text-sm text-muted-foreground">{message}</span>
      <span className="sr-only">{message}</span>
    </div>
  );
}

/**
 * Full-page loading fallback
 */
export function PageLoadingFallback({ message = 'Loading page...' }: { message?: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <LoadingFallback message={message} size="lg" />
    </div>
  );
}

/**
 * Inline loading indicator for smaller areas
 */
export function InlineLoading({ message = 'Loading...' }: { message?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-muted-foreground" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">{message}</span>
    </span>
  );
}
